(function (global) {
    'use strict';

    function getHref(link, documentRef) {
        if (!link) return '';
        const rawHref = link.href || (link.getAttribute && link.getAttribute('href'));
        if (!rawHref) return '';
        return new URL(rawHref, documentRef.baseURI || global.location.href).toString();
    }

    function getProductUrls(grid) {
        return Array.from(grid.children || [])
            .map((card) => card.querySelector && card.querySelector('a[href*="/products/"]'))
            .filter(Boolean)
            .map((link) => new URL(link.href, global.location && global.location.href).pathname);
    }

    function setLoadingState(button, isLoading) {
        if (!button) return;
        button.disabled = isLoading;
        if (isLoading) {
            button.setAttribute('aria-busy', 'true');
        } else {
            button.removeAttribute('aria-busy');
        }
    }

    function updateCount(root, count) {
        const collectionApp = root.closest && root.closest('#app');
        const countElements = new Set();

        if (collectionApp && collectionApp.querySelectorAll) {
            collectionApp.querySelectorAll('[data-lr-load-more-count]').forEach((element) => {
                countElements.add(element);
            });

            if (collectionApp.querySelector) {
                const headerCount = collectionApp.querySelector('[data-lr-load-more-header-count]');
                if (headerCount) countElements.add(headerCount);
            }
        } else {
            const countElement = root.querySelector('[data-lr-load-more-count]');
            if (countElement) countElements.add(countElement);
        }

        countElements.forEach((element) => {
            element.textContent = `Showing ${count} of ${root.dataset.totalProducts} products`;
        });
    }

    function loadNextPage(root, environment) {
        if (!root) return Promise.resolve({status: 'skipped'});
        if (root.__lrLoadMorePromise) return root.__lrLoadMorePromise;

        const documentRef = (environment && environment.document) || global.document;
        const fetchImpl = (environment && environment.fetch) || global.fetch;
        const parseHTML = (environment && environment.parseHTML) || ((html) => {
            const parser = new global.DOMParser();
            return parser.parseFromString(html, 'text/html');
        });
        const button = root.querySelector('[data-lr-load-more-button]');
        const nextLink = root.querySelector('[data-lr-next-page]');
        const serverGrid = root.querySelector('#lr-server-rendered-products') ||
            documentRef.querySelector('#lr-server-rendered-products');

        if (!documentRef || !fetchImpl || !nextLink || !serverGrid) {
            return Promise.resolve({status: 'skipped'});
        }

        const request = (async () => {
            setLoadingState(button, true);

            try {
                const requestUrl = new URL(getHref(nextLink, documentRef));
                if (root.dataset.sectionId) {
                    requestUrl.searchParams.set('section_id', root.dataset.sectionId);
                }

                const response = await fetchImpl(requestUrl.toString(), {
                    headers: {Accept: 'text/html'}
                });
                if (!response || !response.ok) {
                    throw new Error(`Collection page request failed: ${response && response.status}`);
                }

                const html = await response.text();
                const parsedDocument = parseHTML(html);
                const incomingGrid = parsedDocument && parsedDocument.querySelector('#lr-server-rendered-products');
                const incomingCards = Array.from((incomingGrid && incomingGrid.children) || []);
                if (!incomingCards.length) {
                    throw new Error('Collection page did not contain product cards');
                }

                const existingUrls = new Set(getProductUrls(serverGrid));
                const incomingUrls = getProductUrls({children: incomingCards});
                if (incomingUrls.length !== incomingCards.length || incomingUrls.some((url, index) => {
                    return existingUrls.has(url) || incomingUrls.indexOf(url) !== index;
                })) {
                    throw new Error('Collection page contained duplicate or invalid product cards');
                }

                incomingCards.forEach((card) => {
                    serverGrid.append(card.cloneNode ? card.cloneNode(true) : card);
                });

                const incomingNextLink = parsedDocument.querySelector('[data-lr-next-page]');
                const incomingHref = getHref(incomingNextLink, documentRef);
                if (incomingHref) {
                    nextLink.href = incomingHref;
                    if (nextLink.setAttribute) nextLink.setAttribute('href', incomingHref);
                } else {
                    nextLink.hidden = true;
                    if (button) button.hidden = true;
                }

                root.dataset.lrLoadMoreError = 'false';
                updateCount(root, serverGrid.children.length);
                return {status: 'loaded', count: serverGrid.children.length};
            } catch (error) {
                root.dataset.lrLoadMoreError = 'true';
                nextLink.hidden = false;
                throw error;
            } finally {
                setLoadingState(button, false);
                root.__lrLoadMorePromise = null;
            }
        })();

        root.__lrLoadMorePromise = request;
        return request;
    }

    function init(root, environment) {
        if (!root || root.__lrLoadMoreInitialized) return root;
        const button = root.querySelector('[data-lr-load-more-button]');
        const nextLink = root.querySelector('[data-lr-next-page]');
        root.__lrLoadMoreInitialized = true;

        if (nextLink) nextLink.hidden = true;
        const serverGrid = root.querySelector('#lr-server-rendered-products');
        if (serverGrid) updateCount(root, serverGrid.children.length);

        if (root.addEventListener) {
            root.addEventListener('click', (event) => {
                const clickedButton = event.target.closest && event.target.closest('[data-lr-load-more-button]');
                if (!clickedButton) return;

                loadNextPage(root, environment).catch((error) => {
                    if (global.console && global.console.error) {
                        global.console.error('Collection Load More failed', error);
                    }
                });
            });
        }
        return root;
    }

    let rootObserver;

    function initAll() {
        if (!global.document) return;
        const roots = global.document.querySelectorAll('[data-lr-collection-load-more]');
        roots.forEach(init);
        if (roots.length && rootObserver) {
            rootObserver.disconnect();
            rootObserver = null;
        }
    }

    if (global.document) {
        initAll();
        if (global.document.readyState === 'loading') {
            global.document.addEventListener('DOMContentLoaded', initAll);
        }
        global.document.addEventListener('shopify:section:load', initAll);
        if (global.MutationObserver && global.document.documentElement) {
            rootObserver = new global.MutationObserver(initAll);
            rootObserver.observe(global.document.documentElement, {childList: true, subtree: true});
        }
    }

    const api = {init, initAll, loadNextPage};
    global.LrCollectionLoadMore = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
